import { SEARCH_ICON_SVG } from '../constants';
import type MultiSelect from '../index';

export class Search {
    private readonly ms: MultiSelect;
    public searchInput: HTMLInputElement | null = null;
    public searchClearBtn: HTMLButtonElement | null = null;
    public searchIcon: HTMLElement | null = null;

    constructor(multiSelect: MultiSelect) {
        this.ms = multiSelect;
    }

    public createMenuSearchHtml(): string {
        return `
            <div class="search-input-container">
                <input type="text" class="form-control form-control-sm" placeholder="Search...">
                <span class="search-icon">${SEARCH_ICON_SVG}</span>
                <button type="button" class="search-clear-btn" aria-label="Clear search" style="display: none;">&times;</button>
            </div>
        `;
    }

    public createColumnsSearchContainer(): HTMLDivElement {
        const container = document.createElement('div');
        container.className = 'columns-search-container';
        container.innerHTML = `
            <input type="text" class="form-control form-control-sm" placeholder="Search...">
            <span class="search-icon">${SEARCH_ICON_SVG}</span>
            <button type="button" class="search-clear-btn" aria-label="Clear search" style="display: none;">&times;</button>
        `;
        return container;
    }

    public initFromContainer(container: HTMLElement): void {
        this.searchInput = container.querySelector<HTMLInputElement>('input[type="text"]');
        this.searchClearBtn = container.querySelector<HTMLButtonElement>('.search-clear-btn');
        this.searchIcon = container.querySelector<HTMLElement>('.search-icon');
    }

    public bindTagsEvents(): void {
        this.bindCoreEvents((searchTerm) => {
            this.ms.menu?.querySelectorAll<HTMLElement>('.form-check-label').forEach((label) => {
                const option = label.closest('.form-check') as HTMLElement | null;
                
                if (option) {
                    const matches = label.textContent?.toLowerCase().includes(searchTerm) ?? false;
                    option.classList.toggle('d-none', !matches);
                }
            });
        });
    }

    public bindColumnsEvents(): void {
        this.bindCoreEvents((searchTerm) => {
            // 1. Filter individual items based on text match
            this.ms.columnsContainer?.querySelectorAll<HTMLElement>('.column-item').forEach((item) => {
                const matches = item.textContent?.toLowerCase().includes(searchTerm) ?? false;
                item.classList.toggle('d-none', !matches);
            });

            // 2. Hide optgroup headers that have no visible items beneath them
            this.ms.columnsContainer?.querySelectorAll<HTMLElement>('.optgroup-header').forEach((header) => {
                let hasVisibleItem = false;
                let sibling = header.nextElementSibling;

                while (sibling && !sibling.classList.contains('optgroup-header')) {
                    if (
                        sibling.classList.contains('column-item')
                        && !sibling.classList.contains('d-none')
                    ) {
                        hasVisibleItem = true;
                        break;
                    }
                    sibling = sibling.nextElementSibling;
                }

                header.classList.toggle('d-none', !hasVisibleItem);
            });
        });
    }

    private bindCoreEvents(filterFn: (searchTerm: string) => void): void {
        if (!this.searchInput || !this.searchClearBtn || !this.searchIcon) {
            return;
        }

        const { searchInput, searchClearBtn, searchIcon } = this;

        searchInput.addEventListener('input', () => {
            const searchTerm = searchInput.value.toLowerCase();
            const hasValue = searchTerm.length > 0;

            searchClearBtn.style.display = hasValue ? 'block' : 'none';
            searchIcon.style.display = hasValue ? 'none' : 'block';

            filterFn(searchTerm);

            if (this.ms.isCreatable) {
                this.ms.creatableFeature.showCreateOption(searchInput.value);
            }
        });

        searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();

                if (this.ms.isCreatable && this.ms.creatableFeature.handleEnterKey()) {
                    return;
                }
            }
        });

        searchClearBtn.addEventListener('click', () => {
            searchInput.value = '';
            searchInput.dispatchEvent(new Event('input'));
            searchInput.focus();
        });
    }

    public reset(): void {
        if (this.searchInput && this.searchClearBtn && this.searchIcon) {
            this.searchInput.value = '';
            this.searchClearBtn.style.display = 'none';
            this.searchIcon.style.display = 'block';
            this.searchInput.dispatchEvent(new Event('input'));
        }
    }
}